/**
 * User Agent Parser Module
 *
 * Provides lazy-loaded device, browser, and OS detection using Bowser.
 * This module uses dynamic imports to ensure Bowser is only loaded when needed,
 * reducing the initial bundle size for pages that don't use device targeting.
 *
 * The Bowser library is loaded on-demand when any detection function is called.
 * Webpack will create a separate chunk (ua-parser) for the Bowser library.
 */

import type { DeviceType, BrowserType, OSType } from '../types/targeting';

// Type for Bowser module
type BowserModule = typeof import('bowser');
type BowserParser = import('bowser').Parser.Parser;

// Cached Bowser module and parser instance
let bowserModule: BowserModule | null = null;
let cachedParser: BowserParser | null = null;
let cachedUserAgent: string | null = null;

/**
 * Lazily load the Bowser library.
 * Uses webpack magic comment to create a separate chunk.
 */
async function loadBowser(): Promise<BowserModule> {
	if (!bowserModule) {
		bowserModule = await import(
			/* webpackChunkName: "ua-parser" */
			'bowser'
		);
	}
	return bowserModule;
}

/**
 * Get or create a cached Bowser parser for the given user agent.
 * The parser is cached per user agent to avoid re-parsing.
 */
async function getParser(userAgent: string): Promise<BowserParser> {
	// Return cached parser if user agent hasn't changed
	if (cachedParser && cachedUserAgent === userAgent) {
		return cachedParser;
	}

	const Bowser = await loadBowser();
	cachedParser = Bowser.getParser(userAgent);
	cachedUserAgent = userAgent;
	return cachedParser;
}

/**
 * detect device type from user agent using Bowser ().
 * Bowser is lazy-loaded on first call.
 */
export async function detectDevice(userAgent: string): Promise<DeviceType> {
	const parser = await getParser(userAgent);
	const platformType = parser.getPlatformType();

	switch (platformType) {
		case 'mobile':
			return 'mobile';
		case 'tablet':
			return 'tablet';
		default:
			return 'desktop';
	}
}

/**
 * detect browser from user agent using Bowser ().
 * Bowser is lazy-loaded on first call.
 */
export async function detectBrowser(userAgent: string): Promise<BrowserType> {
	const parser = await getParser(userAgent);
	const browserName = parser.getBrowserName()?.toLowerCase() || '';

	if (browserName.includes('chrome')) {
		return 'chrome';
	}
	if (browserName.includes('firefox')) {
		return 'firefox';
	}
	if (browserName.includes('safari')) {
		return 'safari';
	}
	if (browserName.includes('edge')) {
		return 'edge';
	}
	if (browserName.includes('opera')) {
		return 'opera';
	}
	if (
		browserName.includes('internet explorer') ||
		browserName.includes('ie')
	) {
		return 'ie';
	}

	return 'unknown';
}

/**
 * detect OS from user agent using Bowser ().
 * Bowser is lazy-loaded on first call.
 */
export async function detectOS(userAgent: string): Promise<OSType> {
	const parser = await getParser(userAgent);
	const osName = parser.getOSName()?.toLowerCase() || '';

	if (osName.includes('ios')) {
		return 'ios';
	}
	if (osName.includes('android')) {
		return 'android';
	}
	if (osName.includes('macos') || osName.includes('mac os')) {
		return 'macos';
	}
	if (osName.includes('windows')) {
		return 'windows';
	}
	if (osName.includes('linux')) {
		return 'linux';
	}

	return 'unknown';
}

/**
 * Preload Bowser library without blocking.
 * Call this early if you know device detection will be needed.
 */
export function preloadUAParser(): void {
	loadBowser().catch(() => {
		// Silently ignore preload errors
	});
}

/**
 * Clear the cached parser (useful for testing or when user agent changes).
 */
export function clearParserCache(): void {
	cachedParser = null;
	cachedUserAgent = null;
}

/**
 * Check if Bowser has been loaded.
 * Useful for debugging and testing.
 */
export function isBowserLoaded(): boolean {
	return bowserModule !== null;
}
